// src/hooks/useClientForm.js
// ============================================================
// 🔥 V1.0 ENTERPRISE — Hook Universal de Formulario de Cliente
// ============================================================
// 🏆 Arquitectura: Salesforce / HubSpot / Monday.com level
// ✅ Single hook para: quick, detailed, edit
// ✅ Validación en tiempo real con debounce
// ✅ Formateo automático (teléfono, CPF, CEP, moneda)
// ✅ Anti-doble-submit con isSavingRef
// ✅ Rollback automático si falla el guardado
// ✅ Memoizado para rendimiento extremo
// ✅ Sin dependencias innecesarias
// ============================================================
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  CLIENT_DEFAULTS,
  CLIENT_FIELDS,
  CLIENT_FORM_LEVELS,
  validateClientField,
  formatPhoneDisplay,
  formatCPFDisplay,
  formatCEPDisplay,
  normalizePhoneBR,
} from "../config/clientFormConfig";

// ============================================================
// 🛠️ HELPERS PRIVADOS
// ============================================================

/**
 * Formatea el valor según el tipo de campo antes de guardar
 */
const sanitizeForSave = (key, value) => {
  const field = CLIENT_FIELDS[key];
  if (!field) return value;

  switch (field.type) {
    case "tel":
    case "phone":
      return normalizePhoneBR(value);
    
    case "currency":
      return value ? String(value).replace(/\D/g, "") : "";
    
    case "number":
      return value ? Number(value) : "";
    
    case "tags":
      return Array.isArray(value) ? value : [];
    
    case "text":
    case "email":
    case "textarea":
      return String(value || "").trim();
    
    default:
      return value;
  }
};

/**
 * Aplica máscara de visualización según el tipo de campo
 */
const applyDisplayMask = (key, value) => {
  const field = CLIENT_FIELDS[key];
  if (!field?.mask || !value) return value;

  switch (field.mask) {
    case "phone":
      return formatPhoneDisplay(value);
    case "cpf":
      return formatCPFDisplay(value);
    case "cep":
      return formatCEPDisplay(value);
    default:
      return value;
  }
};

/**
 * Obtiene el valor puro (sin máscara) para validación
 */
const getRawValue = (key, value) => {
  const field = CLIENT_FIELDS[key];
  if (!field) return value;

  switch (field.mask) {
    case "phone":
    case "cpf":
    case "cep":
    case "currency":
      return String(value || "").replace(/\D/g, "");
    default:
      return value;
  }
};

// ============================================================
// 🎯 HOOK PRINCIPAL
// ============================================================
export function useClientForm({
  level = "quick",
  initialData = null,
  onSuccess,
  onError,
  dataContextRef,
  showToast,
  addNotification,
}) {
  // ==========================================================
  // 📋 CONFIGURACIÓN SEGÚN NIVEL
  // ==========================================================
  const formConfig = useMemo(() => {
    const config = CLIENT_FORM_LEVELS[level];
    if (!config) {
      console.warn(`⚠️ [useClientForm] Nivel "${level}" no encontrado, usando "quick"`);
      return CLIENT_FORM_LEVELS.quick;
    }
    return config;
  }, [level]);

  // ==========================================================
  // 🏗️ ESTADO INICIAL
  // ==========================================================
  const getInitialForm = useCallback(() => {
    if (initialData && level === "edit") {
      // Para edición: merge de defaults con datos existentes
      return {
        ...CLIENT_DEFAULTS,
        ...initialData,
        tags: initialData.tags || [],
      };
    }
    // Para creación: solo defaults
    return { ...CLIENT_DEFAULTS };
  }, [initialData, level]);

  // ==========================================================
  // 📊 ESTADOS
  // ==========================================================
  const [form, setForm] = useState(getInitialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Refs para anti-doble-click y valores previos
  const isSavingRef = useRef(false);
  const previousFormRef = useRef(null);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  // ==========================================================
  // 🧹 CLEANUP AL DESMONTAR
  // ==========================================================
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // ==========================================================
  // 🔄 RESET FORM CUANDO CAMBIA initialData (modo edit)
  // ==========================================================
  useEffect(() => {
    if (initialData && level === "edit") {
      setForm({
        ...CLIENT_DEFAULTS,
        ...initialData,
        tags: initialData.tags || [],
      });
      previousFormRef.current = { ...initialData };
      setDirty(false);
      setTouched({});
      setErrors({});
    }
  }, [initialData?.id]); // Solo si cambia el ID del cliente

  // ==========================================================
  // ✏️ MANEJADOR DE CAMBIOS (con formateo automático)
  // ==========================================================
  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;
    
    // Obtener valor según tipo de input
    let newValue;
    if (type === "checkbox") {
      newValue = e.target.checked;
    } else if (type === "select-one") {
      newValue = value;
    } else {
      newValue = value;
    }

    setForm(prev => {
      const updated = { ...prev, [name]: newValue };
      
      // Marcar como dirty si cambió respecto al original (modo edit)
      if (level === "edit" && previousFormRef.current) {
        const original = previousFormRef.current[name];
        const current = getRawValue(name, newValue);
        const originalRaw = getRawValue(name, original);
        
        if (current !== originalRaw) {
          setDirty(true);
        }
      } else {
        setDirty(true);
      }

      return updated;
    });

    // Marcar como tocado
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }

    // Validación en tiempo real con debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      
      const rawValue = getRawValue(name, newValue);
      const error = validateClientField(name, rawValue, form);
      
      setErrors(prev => {
        if (error) {
          return { ...prev, [name]: error };
        } else {
          const { [name]: _, ...rest } = prev;
          return rest;
        }
      });
    }, 300); // 300ms debounce para no saturar

  }, [form, touched, level]);

  // ==========================================================
  // 🏷️ MANEJADOR DE TAGS
  // ==========================================================
  const handleAddTag = useCallback((e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      
      setForm(prev => {
        const currentTags = Array.isArray(prev.tags) ? prev.tags : [];
        if (currentTags.includes(newTag)) return prev;
        return { ...prev, tags: [...currentTags, newTag] };
      });
      
      e.target.value = "";
      setDirty(true);
    }
  }, []);

  const handleRemoveTag = useCallback((tagToRemove) => {
    setForm(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
    setDirty(true);
  }, []);

  // ==========================================================
  // 🔍 VALIDACIÓN COMPLETA DEL FORMULARIO
  // ==========================================================
  const validateForm = useCallback(() => {
    const newErrors = {};
    const fieldsToValidate = formConfig.fields === "all"
      ? Object.keys(CLIENT_FIELDS)
      : formConfig.fields;

    fieldsToValidate.forEach(fieldKey => {
      const rawValue = getRawValue(fieldKey, form[fieldKey]);
      const error = validateClientField(fieldKey, rawValue, form);
      if (error) {
        newErrors[fieldKey] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, formConfig]);

  // ==========================================================
  // 💾 MANEJADOR DE SUBMIT (anti-doble-click)
  // ==========================================================
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();

    // Anti-doble-click
    if (isSavingRef.current) {
      console.warn("⚠️ [useClientForm] Submit bloqueado: ya hay un guardado en progreso");
      return;
    }

    // Marcar todos como tocados para mostrar errores
    const allTouched = {};
    const fieldsToValidate = formConfig.fields === "all"
      ? Object.keys(CLIENT_FIELDS)
      : formConfig.fields;
    fieldsToValidate.forEach(key => { allTouched[key] = true; });
    setTouched(prev => ({ ...prev, ...allTouched }));

    // Validar
    if (!validateForm()) {
      if (showToast) {
        showToast("Corrija os campos destacados antes de salvar", "warning");
      }
      return;
    }

    // Guardar
    isSavingRef.current = true;
    setSaving(true);

    try {
      // Sanitizar datos para guardado
      const sanitizedData = {};
      fieldsToValidate.forEach(key => {
        sanitizedData[key] = sanitizeForSave(key, form[key]);
      });

      // Agregar timestamps
      const now = new Date().toISOString();
      sanitizedData.updatedAt = now;

      let result;

      if (level === "edit" && initialData?.id) {
        // Actualizar cliente existente
        const ctx = dataContextRef?.current;
        if (ctx?.collections?.clients?.update) {
          result = await ctx.collections.clients.update(initialData.id, sanitizedData);
        } else if (ctx?.updateClient) {
          result = await ctx.updateClient(initialData.id, sanitizedData);
        } else {
          throw new Error("DataContext no disponible para actualizar");
        }
      } else {
        // Crear nuevo cliente
        sanitizedData.createdAt = now;
        
        const ctx = dataContextRef?.current;
        if (ctx?.collections?.clients?.create) {
          result = await ctx.collections.clients.create(sanitizedData);
        } else if (ctx?.createClient) {
          result = await ctx.createClient(sanitizedData);
        } else {
          throw new Error("DataContext no disponible para crear");
        }
      }

      // Éxito
      if (mountedRef.current) {
        setSaving(false);
        setDirty(false);
        setLastSaved(now);
        
        if (level === "edit") {
          previousFormRef.current = { ...form };
        }

        if (showToast) {
          showToast(
            level === "edit" 
              ? "✅ Cliente atualizado com sucesso!" 
              : "✅ Cliente criado com sucesso!",
            "success"
          );
        }

        if (addNotification) {
          addNotification({
            type: "success",
            title: level === "edit" ? "Cliente atualizado" : "Cliente criado",
            message: `${form.name} ${level === "edit" ? "foi atualizado" : "foi adicionado"}`,
            metadata: { clientId: result?.id || initialData?.id }
          });
        }

        if (onSuccess) {
          onSuccess(result || { ...sanitizedData, id: initialData?.id });
        }
      }

    } catch (error) {
      console.error(`❌ [useClientForm] Error al ${level === "edit" ? "actualizar" : "crear"} cliente:`, error);
      
      if (mountedRef.current) {
        if (showToast) {
          showToast(
            `Erro ao ${level === "edit" ? "atualizar" : "criar"} cliente: ${error.message}`,
            "error"
          );
        }

        if (addNotification) {
          addNotification({
            type: "error",
            title: `Erro ao ${level === "edit" ? "atualizar" : "criar"}`,
            message: error.message || "Tente novamente mais tarde",
          });
        }

        if (onError) {
          onError(error);
        }
      }

      // Rollback: restaurar valores anteriores en modo edit
      if (level === "edit" && previousFormRef.current) {
        setForm({ ...previousFormRef.current });
      }

    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
      isSavingRef.current = false;
    }
  }, [form, formConfig, level, initialData, dataContextRef, showToast, addNotification, onSuccess, onError, validateForm]);

  // ==========================================================
  // 🔄 RESET DEL FORMULARIO
  // ==========================================================
  const handleReset = useCallback(() => {
    const defaults = { ...CLIENT_DEFAULTS };
    if (level === "edit" && initialData) {
      setForm({ ...defaults, ...initialData, tags: initialData.tags || [] });
      previousFormRef.current = { ...initialData };
    } else {
      setForm(defaults);
    }
    setErrors({});
    setTouched({});
    setDirty(false);
    setLastSaved(null);
  }, [level, initialData]);

  // ==========================================================
  // 📝 SETTERS ADICIONALES
  // ==========================================================
  const setFieldValue = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    setDirty(true);
  }, []);

  const setMultipleFields = useCallback((fields) => {
    setForm(prev => ({ ...prev, ...fields }));
    const touchedFields = Object.keys(fields).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(prev => ({ ...prev, ...touchedFields }));
    setDirty(true);
  }, []);

  // ==========================================================
  // 📊 GETTERS COMPUTADOS
  // ==========================================================
  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  const canSubmit = useMemo(() => {
    return isValid && !saving && (level !== "edit" || dirty);
  }, [isValid, saving, dirty, level]);

  const displayValues = useMemo(() => {
    const display = {};
    Object.keys(form).forEach(key => {
      display[key] = applyDisplayMask(key, form[key]);
    });
    return display;
  }, [form]);

  const hasUnsavedChanges = useMemo(() => {
    return dirty && level === "edit";
  }, [dirty, level]);

  // ==========================================================
  // 📤 RETORNO DEL HOOK
  // ==========================================================
  return {
    // Estado
    form,
    errors,
    touched,
    saving,
    dirty,
    lastSaved,
    isValid,
    canSubmit,
    displayValues,
    hasUnsavedChanges,

    // Configuración
    formConfig,
    fieldKeys: formConfig.fields === "all" 
      ? Object.keys(CLIENT_FIELDS) 
      : formConfig.fields,

    // Handlers
    handleChange,
    handleSubmit,
    handleReset,
    handleAddTag,
    handleRemoveTag,

    // Setters
    setFieldValue,
    setMultipleFields,

    // Utilidades
    validateForm,
    applyDisplayMask,
    getRawValue,
  };
}

// ============================================================
// 📤 EXPORTACIÓN POR DEFECTO
// ============================================================
export default useClientForm;